import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAICoach } from './AICoachContext';
import { Alert, Button, ChatMessageView, Checkbox, Icon, Loading, Select, Skeleton } from '@aidkitorg/component-library';
import { safeParse, useInterval, useMarkdown } from '../Util';
import { ConversationItemWithAKMeta } from '@aidkitorg/types/lib/convoai/conversation';
import { useLocalizedStrings } from '../Localization';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { useApplicant } from '../Contexts/ApplicantContext';
import { useContext } from 'react';
import { PublicConfigurationContext } from '../Context';
import { useModularMarkdown } from '../Hooks/ModularMarkdown';
import { FeedbackButtons } from './FeedbackButtons';

// Default values in case config is not provided
const DEFAULT_AGENT_NAME = "Opal";
const DEFAULT_AVATAR_URL = "https://aidkit-public.s3.us-east-2.amazonaws.com/demo/Opal.jpg";

export function AICoachButton(props: { onClick: () => void }) {
  const L = useLocalizedStrings();
  const config = useContext(PublicConfigurationContext);
  const agentName = config?.capabilities?.aiCoach?.appearance?.name || DEFAULT_AGENT_NAME;

  return <Button
    onClick={() => props.onClick()}
    className="pointer-events-auto"
    style={
      {
        borderRadius: "4rem"
      }
    }
    aria-label={L.ai_coach.ask_your_ai_coach.replace("$name", agentName)}
  >
    <span className="text-xs flex items-center gap-1">
      <Icon name="ChatBubbleLeftRightIcon" role="decorative" className="text-white" size="sm" />
      <span className="hidden lg:block">{L.ai_coach.ask_name.replace("$name", agentName)}</span>
    </span>
  </Button>
}

const AudioInputDeviceKey = "audio_input_device";
function AudioInputSelector(props: {
  onSelectDevice: (result: { device: MediaStream | null }) => void,
  onError: (err: any) => void
}) {
  const L = useLocalizedStrings();
  const savedDeviceInfo = safeParse(sessionStorage.getItem(AudioInputDeviceKey)!, null) as MediaDeviceInfo;
  const [availableDeviceInfos, setAvailableDeviceInfos] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceInfo, setSelectedDeviceInfo] = useState<MediaDeviceInfo | null>(savedDeviceInfo ?? null);
  const [remember, setRemember] = useState<boolean>(true);
  const [selectedOption, setSelectedOption] = useState<{ value: string, label: string } | null>(null);
  const options = useMemo(
    () => availableDeviceInfos.map(d => ({ value: d.deviceId, label: d.label })),
    [availableDeviceInfos]
  );

  // load available audio input device
  useAsyncEffect(async () => {
    let availableDevices = [];
    try {
      const d = await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      availableDevices = devices.filter(d => d.kind === "audioinput");
      setAvailableDeviceInfos(availableDevices);
      // devices were only accessed to populate the list
      // they can now be stopped
      d.getTracks().forEach(track => track.stop());
    }
    finally {
      if (!availableDevices.length) {
        props.onError("Failed to access audio input devices");
      }
    }
  }, []);

  // user has made their selection, get the MediaStream for that device
  useAsyncEffect(async () => {
    if (selectedDeviceInfo?.deviceId) {
      try {
        const device = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { exact: selectedDeviceInfo.deviceId }
          }
        });
        if (!device) {
          throw new Error(`Could not find device for device id ${selectedDeviceInfo.deviceId}`);
        }
        // success callback
        props.onSelectDevice({ device });

        // save to local storage
        if (remember && typeof selectedDeviceInfo.toJSON === "function") {
          sessionStorage.setItem(AudioInputDeviceKey, JSON.stringify(selectedDeviceInfo.toJSON()));
        }
      }
      catch (err) {
        console.error(err);
        setSelectedDeviceInfo(null);
        props.onError("Failed to get device");
      }
    }
  }, [remember, selectedDeviceInfo]);

  // set default selected option
  useEffect(() => {
    if (options.length && !selectedOption) {
      setSelectedOption(options[0]);
    }
  }, [selectedOption, options]);

  if (!options.length || selectedDeviceInfo) {
    return <Loading />
  }

  return <form className="flex flex-col gap-2" onSubmit={e => {
    e.preventDefault();
    const selectedDeviceInfo = availableDeviceInfos.find(d => d.deviceId === selectedOption?.value) ?? null;
    if (selectedDeviceInfo) {
      setSelectedDeviceInfo(selectedDeviceInfo);
    }
  }}>
    <div>
      <Select
        label={L.ai_coach.microphone}
        labelLocation="above"
        anchor="top"
        description={L.ai_coach.select_microphone}
        value={selectedOption || options[0]}
        onChange={option => setSelectedOption(option as typeof selectedOption)}
        options={options} />
    </div>
    <div className="flex gap-2 items-center">
      <Checkbox
        label={L.ai_coach.remember}
        checked={remember}
        onChange={v => setRemember(v as boolean)}
      />
      <Button
        className="grow"
        type="submit"
        icon={<Icon name="CheckCircleIcon" type="solid" size="xl" />}
        isDisabled={!options.length}>
      </Button>
    </div>
  </form>
}

const MessageContent = ({ message, showFeedbackButtons }: { message: ConversationItemWithAKMeta, showFeedbackButtons?: boolean }) => {
  if (message.meta.phase === "in_process") {
    return <Icon name="ChatBubbleOvalLeftEllipsisIcon" />;
  }
  if (message.meta.phase === "pending") {
    return <Icon name="EllipsisHorizontalIcon" />;
  }
  const content = message.content?.map(c => c.text ?? c.transcript).join('');
  return <div className="coach-chat-content flex flex-col gap-2 divide-y divide-solid divide-indigo-200">
    {useMarkdown(content)}
    {(message.role === "assistant" && showFeedbackButtons) && <div className="font-normal px-3 pt-2 -mx-4">
      <FeedbackButtons id={message.id!} {...message.meta?.feedback ?? {}} />
    </div>}
  </div>
};

//
// function AudioInputView(props: { onClick(): void }) {
//   const [chatStart, setChatStart] = useState(new Date());
//
//   return <div className="border-t border-gray-200 p-3">
//     <div className="flex items-center gap-2">
//       <Button
//         variant={"danger"}
//         shape="circle"
//         aria-label={L.ai_coach.voice_input}
//         onClick={props.onClick}
//       >
//         <Icon name="XMarkIcon" />
//       </Button>
//
//       <span>{moment(chatStart).diff(Date.now())}</span>
//     </div>
//   </div>
// }
//
// function TextInputView(props: {}) {
//   return <div className="border-t border-gray-200 p-3">
//     <div className="flex items-center gap-2">
//       {/* Microphone button */}
//       <Button
//         variant={"secondary"}
//         shape="circle"
//         isDisabled={failedToGetAudioInput}
//         aria-label={L.ai_coach.voice_input}
//         onClick={() => setModality("audio")}
//       >
//         <Icon name="MicrophoneIcon" />
//       </Button>
//
//       <form
//         className="flex flex-1 gap-2"
//         onSubmit={handleChatSubmit}>
//         <input
//           type="text"
//           className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
//           placeholder={`${L.ai_coach.type_a_message}...`}
//           value={inputText}
//           onChange={(e) => setInputText(e.target.value)}
//         />
//         <Button
//           type="submit"
//           isDisabled={!inputText || !active}
//         >
//           <Icon
//             className="-rotate-45 -mt-1 -mr-1"
//             name="PaperAirplaneIcon" />
//         </Button>
//       </form>
//     </div>
//   </div>
// }

export function AICoachChatWindow(props: { onClickMinimize: () => void }) {
  const L = useLocalizedStrings();
  const config = useContext(PublicConfigurationContext);
  const agentName = config?.capabilities?.aiCoach?.appearance?.name || DEFAULT_AGENT_NAME;
  const avatarUrl = config?.capabilities?.aiCoach?.appearance?.avatarUrl || DEFAULT_AVATAR_URL;
  const [inputText, setInputText] = useState<string>("");
  const [showAudioInputSelection, setShowAudioInputSelection] = useState<boolean>(false);
  const [remember, setRemember] = useState<boolean>(false);
  const [failedToGetAudioInput, setFailedToGetAudioInput] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [activeKeys, setActiveKeys] = useState<Set<string>>(new Set());
  const chatForm = useRef<HTMLFormElement | null>(null);

  const [chatAudioStart, setChatAudioStart] = useState<number>(Date.now());
  const [chatAudioDuration, setChatAudioDuration] = useState<number>(0);

  const {
    transcript,
    hasAcceptedTerms,
    setHasAcceptedTerms,
    active,
    modality,
    setModality,
    audioInputStream,
    startAudioInput,
    setAudioElement,
    chatChannel
  } = useAICoach();
  const { info } = useApplicant();

  useEffect(() => { console.log('INFO', info) }, [info]);

  useEffect(() => {
    if (modality === "audio") {
      setChatAudioStart(Date.now());
      setAudioElement(audioRef.current);
      setShowAudioInputSelection(true);
    }
    else {
      setAudioElement(null);
      setShowAudioInputSelection(false)
    }
  }, [modality, audioRef.current])

  useInterval(() => setChatAudioDuration(Math.floor((Date.now() - chatAudioStart) / 1000)), 1);

  const chatAudioDurationDisplay = useMemo(() => {
    const seconds = chatAudioDuration;

    if (!Number.isFinite(seconds) || seconds < 0) {
      return '00:00:00';
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    const pad = (num: number): string => num.toString().padStart(2, '0');

    return `${pad(hours)}:${pad(minutes)}:${pad(remainingSeconds)}`;
  }, [chatAudioDuration]);

  const handleChatSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    if (inputText) {
      setModality("text");
      chatChannel?.sendChatMessage(inputText, { role: 'user', createResponse: true, allowAudio: false });
    }
    setInputText('');
  }

  const messageBubbles: Parameters<typeof ChatMessageView>[0]['transcript'] = useMemo(() => {
    let items: Parameters<typeof ChatMessageView>[0]['transcript'] = [];

    items = items.concat(transcript.filter(t => !t?.meta?.hidden).map((m, i, arr) => ({
      role: m.role,
      meta: m.meta,
      content: <MessageContent message={m} showFeedbackButtons={m.role === "assistant"} />
    })));

    return items;
  }, [transcript]);
  
  const phase = useMemo(() => chatChannel?.state.phase, [chatChannel?.state.phase]);

  const termsContent = useModularMarkdown({
    content: L.ai_coach.terms_full,
    info: {
      name: agentName,
      termsofservice: '[Terms of Service](https://www.rocketlawyer.com/sem/artificial-intelligence-terms-of-service)',
      privacypolicy: '[Privacy Policy](/privacy)'
    }
  });

  /* Sliding chat panel - full screen on mobile, partial on desktop */
  return <div className="fixed bottom-0 right-0 z-50 w-full sm:w-80 md:w-[360px] lg:w-[450px] max-w-[100vw] sm:max-w-[90vw] 
                      h-full sm:h-[90vh] md:h-[75vh] bg-white shadow-lg flex flex-col sm:rounded-t-lg">
    {/* Header */}
    <div className="border-b border-gray-200">
      <div className="flex flex-col p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <img src={avatarUrl} alt={agentName} className="w-12 h-12 rounded-full mr-3" />
            <h3 className="text-2xl font-semibold mt-2">{L.ai_coach.chat_with.replace('$name', agentName)}</h3>
          </div>
          {/* Minimize button */}
          <div className="group relative">
            <Button
              variant="secondary"
              className="p-2"
              onClick={props.onClickMinimize}
              aria-label={L.ai_coach.minimize_chat}
            >
              <Icon name="ArrowsPointingInIcon" />
              <div className="absolute bottom-full right-0 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {L.ai_coach.minimize_chat}
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>

    {/* Loading skeleton */}
    {(chatChannel?.enabled && !chatChannel?.channelIsReady && !chatChannel?.state?.error) && <Skeleton className="p-4" variant="sentences" size="md" />}

    <ChatMessageView
      transcript={messageBubbles}
      roles={{
        assistant: {
          side: 'left',
          color: 'bg-indigo-100',
          icon: <img src={avatarUrl} alt={agentName} className="rounded-full" />
        },
        user: {
          side: 'right',
          icon: <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center font-semibold text-white">{info.legal_name?.substring(0, 1)?.toUpperCase() || 'U'}</div>,
          color: 'bg-gray-100',
        }
      }}
      autoScroll={true}
    />

    {/* Terms of Service - shown only before acceptance */}
    {!hasAcceptedTerms && (
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <h4 className="font-semibold text-gray-800 mb-2">{L.terms_and_conditions}</h4>
        <>{termsContent}</>
        <Button
          onClick={() => setHasAcceptedTerms(true)}
          className="w-full mt-2">
          {L.ai_coach.accept_terms}
        </Button>
      </div>
    )}

    {/* Error */}
    {(chatChannel?.state?.error) && <div className="px-2 pb-2">
      <Alert
        className="flex flex-col gap-2 p-3"
        dismissible={true}
        variant="danger">
        <div>{L.ai_coach.coach_error_message}</div>
        <Button
          size="sm"
          onClick={() => location.reload()}>
          {L.applicant.history.refresh}
        </Button>
      </Alert>
    </div>}

    {(showAudioInputSelection && (!audioInputStream || !remember) && !failedToGetAudioInput) && <div className="border-t bg-gray-50 border-gray-200 p-3">
      <AudioInputSelector
        onSelectDevice={
          ({ device }) => {
            if (device) {
              startAudioInput(device);
              setFailedToGetAudioInput(false);
              setShowAudioInputSelection(false);
            }
          }
        }
        onError={
          () => setFailedToGetAudioInput(true)
        } />
    </div>}

    {/* Message input - only shown after terms acceptance */}
    {hasAcceptedTerms && (
      <div className="border-t border-gray-200 p-2 md:p-3">
        <div hidden={modality === 'text'} className="flex w-full">
          <div className="w-[80%] flex space-x-4">
            <Button
              variant={"danger"}
              shape="circle"
              aria-label={L.ai_coach.voice_input}
              onClick={() => setModality('text')}
            >
              <Icon name="XMarkIcon" />
            </Button>
            <div className="content-center">{chatAudioDurationDisplay}</div>
          </div>
          <div className="content-center end-1 w-[20%]">
            {L.ai_coach.phases[phase ?? 'listening']}
          </div>
        </div>
        <form
          ref={chatForm}
          hidden={modality === 'audio'}
          className="flex items-center gap-2"
          onSubmit={handleChatSubmit}>
          {/* Microphone button */}
          <Button
            className="shrink-0"
            variant={(modality === "audio" && audioInputStream) ? "primary" : "secondary"}
            shape="circle"
            isDisabled={failedToGetAudioInput}
            aria-label={L.ai_coach.voice_input}
            onClick={() => setModality("audio")}
          >
            <Icon name="MicrophoneIcon" />
          </Button>
          <textarea
            className="flex-1 shrink-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            rows={1}
            placeholder={`${L.ai_coach.type_a_message}...`}
            value={inputText}
            onKeyDown={e => setActiveKeys(activeKeys => activeKeys.add(e.key))}
            onKeyUp={e => {
              setActiveKeys(activeKeys => {
                activeKeys.delete(e.key);
                return activeKeys;
              });

              // allow enter if shift is being pressed, otherwise have it submit the form
              if (e.key === "Enter" && !activeKeys.has('Shift') && chatForm.current) {
                chatForm.current.dispatchEvent(
                  new Event("submit", { cancelable: true, bubbles: true })
                );
              }
            }}
            onChange={(e) => setInputText(e.target.value)}
          />
          <Button
            type="submit"
            className="shrink-0 px-2"
            isDisabled={!inputText || !active}
          >
            <Icon
              className="-rotate-45 origin-[50%_40%]"
              name="PaperAirplaneIcon" />
          </Button>
        </form>
      </div>
    )}
    <audio ref={audioRef} muted={modality !== "audio"} hidden autoPlay={true} />
  </div>
}
